import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  DollarSign,
  CheckCircle,
  AlertCircle,
  Star,
  TrendingUp
} from 'lucide-react';
import { pricingAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  PricingContainer,
  FloatingElement,
  MainContent,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormSection,
  SectionTitle,
  InputGroup,
  Label,
  Input,
  TextArea,
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  ActionButton,
  PackagesGrid,
  PackageCard,
  PackageHeader,
  MostPopularBadge,
  PointsContainer,
  Point,
  PointInput,
  AddButton,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  ReasonsContainer,
  ReasonInput,
  RequirementsContainer,
  RequirementCard,
  RequirementHeader,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  PreviewSection,
  PreviewTitle,
  PreviewCard,
  CheckboxLabel,
  CheckboxInput
} from './PricingSection.styles';

const PricingSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    page_heading: '',
    page_paragraph: '',
    philosophy_heading: '',
    philosophy_paragraph: '',
    packages: [],
    bulk_sms_pricing: [],
    why_choose: {
      heading: '',
      reasons: []
    },
    requirements: {
      heading: '',
      paragraph: '',
      list: []
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadPricingSection();
  }, []);

  const loadPricingSection = async () => {
    try {
      setLoading(true);
      const response = await pricingAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setExistingData(data);
        setFormData({
          page_heading: data.page_heading || '',
          page_paragraph: data.page_paragraph || '',
          philosophy_heading: data.philosophy_heading || '',
          philosophy_paragraph: data.philosophy_paragraph || '',
          packages: data.packages || [],
          bulk_sms_pricing: data.bulk_sms_pricing || [],
          why_choose: data.why_choose || { heading: '', reasons: [] },
          requirements: data.requirements || { heading: '', paragraph: '', list: [] }
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load pricing data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Package management
  const addPackage = () => {
    const newPackage = {
      title: '',
      amount: '',
      paragraph: '',
      description: '',
      points: ['', '', '', ''],
      is_most_popular: false
    };
    setFormData(prev => ({
      ...prev,
      packages: [...prev.packages, newPackage]
    }));
  };

  const updatePackage = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === index ? { ...pkg, [field]: value } : pkg
      )
    }));
  };

  const updatePackagePoint = (packageIndex, pointIndex, value) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === packageIndex 
          ? {
              ...pkg,
              points: pkg.points.map((point, j) => 
                j === pointIndex ? value : point
              )
            }
          : pkg
      )
    }));
  };

  const removePackage = (index) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  // Bulk SMS Tier management
  const addBulkSmsTier = () => {
    const newTier = {
      quantity_range: '',
      cost_per_unit: '',
      savings_percentage: ''
    };
    setFormData(prev => ({
      ...prev,
      bulk_sms_pricing: [...prev.bulk_sms_pricing, newTier]
    }));
  };

  const updateBulkSmsTier = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      bulk_sms_pricing: prev.bulk_sms_pricing.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const removeBulkSmsTier = (index) => {
    setFormData(prev => ({
      ...prev,
      bulk_sms_pricing: prev.bulk_sms_pricing.filter((_, i) => i !== index)
    }));
  };

  // Why Choose Reasons management
  const addReason = () => {
    setFormData(prev => ({
      ...prev,
      why_choose: {
        ...prev.why_choose,
        reasons: [...prev.why_choose.reasons, '']
      }
    }));
  };

  const updateReason = (index, value) => {
    setFormData(prev => ({
      ...prev,
      why_choose: {
        ...prev.why_choose,
        reasons: prev.why_choose.reasons.map((reason, i) => 
          i === index ? value : reason
        )
      }
    }));
  };

  const removeReason = (index) => {
    setFormData(prev => ({
      ...prev,
      why_choose: {
        ...prev.why_choose,
        reasons: prev.why_choose.reasons.filter((_, i) => i !== index)
      }
    }));
  };

  // Requirements management
  const addRequirement = () => {
    const newRequirement = {
      title: '',
      explanation: ''
    };
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        list: [...prev.requirements.list, newRequirement]
      }
    }));
  };

  const updateRequirement = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        list: prev.requirements.list.map((req, i) => 
          i === index ? { ...req, [field]: value } : req
        )
      }
    }));
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        list: prev.requirements.list.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.page_heading.trim() || !formData.page_paragraph.trim() || 
        !formData.philosophy_heading.trim() || !formData.philosophy_paragraph.trim()) {
      setError('Please fill in all heading and paragraph fields.');
      return;
    }

    if (formData.packages.length === 0) {
      setError('Please add at least one package.');
      return;
    }

    // Validate packages
    for (let i = 0; i < formData.packages.length; i++) {
      const pkg = formData.packages[i];
      if (!pkg.title.trim() || !pkg.amount.trim() || !pkg.paragraph.trim() || !pkg.description.trim()) {
        setError(`Package ${i + 1}: Please fill in all required fields.`);
        return;
      }
      
      if (pkg.points.length !== 4 || pkg.points.some(point => !point.trim())) {
        setError(`Package ${i + 1}: Please provide exactly 4 feature points.`);
        return;
      }
    }

    if (formData.bulk_sms_pricing.length === 0) {
      setError('Please add at least one bulk SMS pricing tier.');
      return;
    }

    if (!formData.why_choose.heading.trim() || formData.why_choose.reasons.length === 0) {
      setError('Please provide a heading and at least one reason for "Why Choose" section.');
      return;
    }

    if (!formData.requirements.heading.trim() || !formData.requirements.paragraph.trim() || 
        formData.requirements.list.length === 0) {
      setError('Please complete the requirements section with heading, paragraph, and at least one requirement.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = existingData 
        ? await pricingAPI.update(formData)
        : await pricingAPI.create(formData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Pricing section updated successfully!' : 'Pricing section created successfully!');
        setExistingData(response.data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save pricing section';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !existingData) {
    return (
      <PricingContainer>
        <MainContent>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <LoadingSpinner />
            <p style={{ color: '#64748B', fontSize: '1rem' }}>Loading pricing data...</p>
          </div>
        </MainContent>
      </PricingContainer>
    );
  }

  return (
    <PricingContainer>
      <FloatingElement delay="0s" />
      <FloatingElement delay="3s" />
      <FloatingElement delay="6s" />

      <MainContent>
        <SectionHeader>
          <Title>Pricing Section</Title>
          <Subtitle>
            Manage your pricing packages, bulk SMS tiers, and onboarding requirements.
          </Subtitle>
        </SectionHeader>

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} />
            {error}
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Page Headers */}
          <FormSection>
            <SectionTitle>Page Headers</SectionTitle>
            
            <InputGroup>
              <Label htmlFor="page_heading">Main Page Heading *</Label>
              <Input
                id="page_heading"
                type="text"
                value={formData.page_heading}
                onChange={(e) => handleInputChange('page_heading', e.target.value)}
                placeholder="e.g., Transparent & Flexible Pricing"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="page_paragraph">Page Introduction *</Label>
              <TextArea
                id="page_paragraph"
                value={formData.page_paragraph}
                onChange={(e) => handleInputChange('page_paragraph', e.target.value)}
                placeholder="Introduce your pricing philosophy..."
                rows={3}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="philosophy_heading">Philosophy Heading *</Label>
              <Input
                id="philosophy_heading"
                type="text"
                value={formData.philosophy_heading}
                onChange={(e) => handleInputChange('philosophy_heading', e.target.value)}
                placeholder="e.g., Our Pricing Philosophy"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="philosophy_paragraph">Philosophy Description *</Label>
              <TextArea
                id="philosophy_paragraph"
                value={formData.philosophy_paragraph}
                onChange={(e) => handleInputChange('philosophy_paragraph', e.target.value)}
                placeholder="Explain your pricing approach..."
                rows={4}
                required
              />
            </InputGroup>
          </FormSection>

          {/* Packages */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>Investment Options / Packages</SectionTitle>
              <AddButton type="button" onClick={addPackage}>
                <Plus size={16} />
                Add Package
              </AddButton>
            </div>

            <PackagesGrid>
              {formData.packages.map((pkg, index) => (
                <PackageCard key={index} isMostPopular={pkg.is_most_popular}>
                  <CardHeader>
                    <CardTitle>Package {index + 1}</CardTitle>
                    {pkg.is_most_popular && <MostPopularBadge><Star size={14} />Most Popular</MostPopularBadge>}
                    <CardActions>
                      <ActionButton 
                        type="button" 
                        onClick={() => removePackage(index)}
                        variant="danger"
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </CardActions>
                  </CardHeader>

                  <InputGroup>
                    <Label>Package Title *</Label>
                    <Input
                      type="text"
                      value={pkg.title}
                      onChange={(e) => updatePackage(index, 'title', e.target.value)}
                      placeholder="e.g., Alphanumeric-Sender ID"
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Amount *</Label>
                    <Input
                      type="text"
                      value={pkg.amount}
                      onChange={(e) => updatePackage(index, 'amount', e.target.value)}
                      placeholder="e.g., KSh 11,600"
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Pricing Note *</Label>
                    <Input
                      type="text"
                      value={pkg.paragraph}
                      onChange={(e) => updatePackage(index, 'paragraph', e.target.value)}
                      placeholder="e.g., VAT Inclusive, one-time per network"
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Description *</Label>
                    <TextArea
                      value={pkg.description}
                      onChange={(e) => updatePackage(index, 'description', e.target.value)}
                      placeholder="Detailed description of the package..."
                      rows={2}
                      required
                    />
                  </InputGroup>

                  <PointsContainer>
                    <Label>Feature Points * (Exactly 4)</Label>
                    {pkg.points.map((point, pointIndex) => (
                      <Point key={pointIndex}>
                        <span style={{ color: '#C5337B', fontWeight: 'bold' }}>•</span>
                        <PointInput
                          type="text"
                          value={point}
                          onChange={(e) => updatePackagePoint(index, pointIndex, e.target.value)}
                          placeholder={`Feature ${pointIndex + 1}`}
                          required
                        />
                      </Point>
                    ))}
                  </PointsContainer>

                  <InputGroup>
                    <CheckboxLabel>
                      <CheckboxInput
                        type="checkbox"
                        checked={pkg.is_most_popular}
                        onChange={(e) => updatePackage(index, 'is_most_popular', e.target.checked)}
                      />
                      Mark as Most Popular
                    </CheckboxLabel>
                  </InputGroup>
                </PackageCard>
              ))}
            </PackagesGrid>

            {formData.packages.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <AddButton type="button" onClick={addPackage}>
                  <Plus size={16} />
                  Add Another Package
                </AddButton>
              </div>
            )}
          </FormSection>

          {/* Bulk SMS Pricing */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>Bulk SMS Pricing Tiers</SectionTitle>
              <AddButton type="button" onClick={addBulkSmsTier}>
                <Plus size={16} />
                Add Tier
              </AddButton>
            </div>

            <Table>
              <thead>
                <TableHeader>
                  <TableCell as="th">Quantity Range</TableCell>
                  <TableCell as="th">Cost per Unit</TableCell>
                  <TableCell as="th">Savings %</TableCell>
                  <TableCell as="th">Actions</TableCell>
                </TableHeader>
              </thead>
              <tbody>
                {formData.bulk_sms_pricing.map((tier, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        type="text"
                        value={tier.quantity_range}
                        onChange={(e) => updateBulkSmsTier(index, 'quantity_range', e.target.value)}
                        placeholder="e.g., 1-50,000"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={tier.cost_per_unit}
                        onChange={(e) => updateBulkSmsTier(index, 'cost_per_unit', e.target.value)}
                        placeholder="e.g., 0.70"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={tier.savings_percentage}
                        onChange={(e) => updateBulkSmsTier(index, 'savings_percentage', e.target.value)}
                        placeholder="e.g., 14% savings or -"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <ActionButton 
                        type="button" 
                        onClick={() => removeBulkSmsTier(index)}
                        variant="danger"
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>

            {formData.bulk_sms_pricing.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <AddButton type="button" onClick={addBulkSmsTier}>
                  <Plus size={16} />
                  Add Another Tier
                </AddButton>
              </div>
            )}
          </FormSection>

          {/* Why Choose Section */}
          <FormSection>
            <SectionTitle>Why Choose Our Services</SectionTitle>

            <InputGroup>
              <Label htmlFor="why_choose_heading">Section Heading *</Label>
              <Input
                id="why_choose_heading"
                type="text"
                value={formData.why_choose.heading}
                onChange={(e) => handleNestedChange('why_choose', 'heading', e.target.value)}
                placeholder="e.g., Why Choose Our Bulk SMS Services?"
                required
              />
            </InputGroup>

            <ReasonsContainer>
              <Label>Reasons (Point Form) *</Label>
              {formData.why_choose.reasons.map((reason, index) => (
                <Point key={index}>
                  <span style={{ color: '#C5337B', fontWeight: 'bold' }}>✓</span>
                  <ReasonInput
                    type="text"
                    value={reason}
                    onChange={(e) => updateReason(index, e.target.value)}
                    placeholder={`Reason ${index + 1}`}
                    required
                  />
                  <ActionButton 
                    type="button" 
                    onClick={() => removeReason(index)}
                    variant="danger"
                  >
                    <Trash2 size={14} />
                  </ActionButton>
                </Point>
              ))}
              <AddButton type="button" onClick={addReason}>
                <Plus size={16} />
                Add Reason
              </AddButton>
            </ReasonsContainer>
          </FormSection>

          {/* Requirements Section */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>Requirements for Onboarding</SectionTitle>
              <AddButton type="button" onClick={addRequirement}>
                <Plus size={16} />
                Add Requirement
              </AddButton>
            </div>

            <InputGroup>
              <Label htmlFor="requirements_heading">Section Heading *</Label>
              <Input
                id="requirements_heading"
                type="text"
                value={formData.requirements.heading}
                onChange={(e) => handleNestedChange('requirements', 'heading', e.target.value)}
                placeholder="e.g., Requirements for Onboarding"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="requirements_paragraph">Introduction Paragraph *</Label>
              <TextArea
                id="requirements_paragraph"
                value={formData.requirements.paragraph}
                onChange={(e) => handleNestedChange('requirements', 'paragraph', e.target.value)}
                placeholder="Introduce the requirements..."
                rows={3}
                required
              />
            </InputGroup>

            <RequirementsContainer>
              {formData.requirements.list.map((req, index) => (
                <RequirementCard key={index}>
                  <RequirementHeader>
                    <span style={{ color: '#C5337B', fontWeight: 'bold', fontSize: '1.2rem' }}>{index + 1}</span>
                    <ActionButton 
                      type="button" 
                      onClick={() => removeRequirement(index)}
                      variant="danger"
                    >
                      <Trash2 size={16} />
                    </ActionButton>
                  </RequirementHeader>

                  <InputGroup>
                    <Label>Title *</Label>
                    <Input
                      type="text"
                      value={req.title}
                      onChange={(e) => updateRequirement(index, 'title', e.target.value)}
                      placeholder="e.g., Company Registration Certificate"
                      required
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Explanation *</Label>
                    <TextArea
                      value={req.explanation}
                      onChange={(e) => updateRequirement(index, 'explanation', e.target.value)}
                      placeholder="Detailed explanation..."
                      rows={2}
                      required
                    />
                  </InputGroup>
                </RequirementCard>
              ))}
            </RequirementsContainer>

            {formData.requirements.list.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <AddButton type="button" onClick={addRequirement}>
                  <Plus size={16} />
                  Add Another Requirement
                </AddButton>
              </div>
            )}
          </FormSection>

          <SubmitButton type="submit" disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner />
                {existingData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save size={20} />
                {existingData ? 'Update Pricing Section' : 'Create Pricing Section'}
              </>
            )}
          </SubmitButton>
        </Form>

        {existingData && (
          <PreviewSection>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewCard>
              <h2 style={{ color: '#2B3857', marginBottom: '0.5rem', textAlign: 'center' }}>
                {formData.page_heading}
              </h2>
              <p style={{ color: '#64748B', marginBottom: '2rem', textAlign: 'center' }}>
                {formData.page_paragraph}
              </p>
              
              <h3 style={{ color: '#2B3857', marginBottom: '0.5rem', textAlign: 'center' }}>
                {formData.philosophy_heading}
              </h3>
              <p style={{ color: '#64748B', marginBottom: '2rem', textAlign: 'center' }}>
                {formData.philosophy_paragraph}
              </p>

              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ color: '#2B3857', marginBottom: '1rem' }}>Packages Preview</h4>
                <p style={{ color: '#64748B', fontSize: '0.9rem' }}>
                  {formData.packages.length} package(s) configured
                </p>
              </div>

              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ color: '#2B3857', marginBottom: '1rem' }}>Bulk SMS Tiers Preview</h4>
                <p style={{ color: '#64748B', fontSize: '0.9rem' }}>
                  {formData.bulk_sms_pricing.length} pricing tier(s) configured
                </p>
              </div>
            </PreviewCard>
          </PreviewSection>
        )}

      </MainContent>
    </PricingContainer>
  );
};

export default PricingSection;

